import { Evaluatie, EvaluatiePunt, useGetDossierEvaluatieById } from "@/entities/evaluatie";
import { DossierSummaryCard, DossierSummaryCardSkeleton } from "@/features/dossier-details";
import { Badge, Dot, EmptyState, ErrorDisplay, Icon, TextLink } from "@/shared/components";

interface EvaluatieSummaryProps {
    dossierNummer: string;
}

export function EvaluatieSummary({ dossierNummer }: Readonly<EvaluatieSummaryProps>) {
    const { data: evaluatie, isPending, error } = useGetDossierEvaluatieById(dossierNummer);

    if (isPending) {
        return <DossierSummaryCardSkeleton section="Evaluatie" />;
    }

    if (error) {
        return <ErrorDisplay error={error} title="Fout bij het laden van evaluatie" />;
    }

    const hasContent = evaluatie != null;

    return (
        <DossierSummaryCard section="Evaluatie" status={evaluatie?.heeftKhrStatus ? "KHR" : ""}>
            {hasContent ? (
                <EvaluatieSummaryContent evaluatie={evaluatie} />
            ) : (
                <EmptyState className="pr-3 pl-4">Geen evaluatie ingevuld</EmptyState>
            )}
        </DossierSummaryCard>
    );
}

type EvaluatieSummaryContentProps = {
    evaluatie: Evaluatie | null;
};

function EvaluatieSummaryContent({ evaluatie }: EvaluatieSummaryContentProps) {
    const evaluatiePunten = Object.entries(evaluatie?.evaluatiePunten ?? {})
        .filter(([, value]) => value?.length > 0)
        .map(([key]) => (
            <Badge key={key}>
                <Icon name="check" size="xs" /> {Evaluatie.getEvaluatiePuntLabel(key as EvaluatiePunt)}
            </Badge>
        ));

    return (
        <div className="grid gap-4 pr-3 pl-4">
            <div className="flex items-center gap-2">
                <span className="text-content-emphasis font-medium">IC-Doc</span>
                <Dot size="s" />
                <p className="text-s text-content-subtle">
                    {evaluatie?.icDocLink ? (
                        <TextLink href={evaluatie.icDocLink} icon="external" target="_blank">
                            Link naar IC-doc dossier
                        </TextLink>
                    ) : (
                        "Nog niet toegevoegd"
                    )}
                </p>
            </div>
            <div className="grid gap-1">
                <h3 className="text-content-emphasis font-medium">Evaluatievelden</h3>
                {evaluatiePunten.length ? (
                    <div className="flex flex-wrap gap-3">{evaluatiePunten}</div>
                ) : (
                    <p className="text-s text-content-subtle">Nog geen evaluatievelden gevuld</p>
                )}
            </div>
        </div>
    );
}
